import { Box, Button, Flex, Heading, Image, Link, Mark, Text } from "@chakra-ui/react"
import { isVsmeMethod } from "@esgt/types"
import frontpageImgSmall from "assets/report-frontpage-small.jpg"
import frontpageImgUrl from "assets/vsme-report-frontpage.png"
import { useHelpPopover } from "components/HelpPopover"
import { Logo } from "components/Logo"
import { formatReportPublishedDate } from "lib/helpers/reportPublishedDate"
import { useReportDownload } from "lib/hooks/useReportDownload/useReportDownload"
import { RatingAccount } from "lib/providers"
import { useRatingAccount } from "lib/providers/RatingAccount"
import { useRating } from "lib/providers/RatingProvider"
import { FreeVersionWatermark } from "pages/Report/components"
import { MdDownload } from "react-icons/md"

export const Results: React.FC = () => {
  const { ratingId, ratingState, isFree, method } = useRating()
  const downloadReport = useReportDownload(ratingId)
  const { setHelpPopoverState } = useHelpPopover()

  return (
    ratingState.phase === "COMPLETED" && (
      <RatingAccount.Provider ratingId={ratingId}>
        <Box maxW="1200px" m="auto" mt="16">
          <Flex gap="8">
            <Box flex="0 0 450px" border="1px solid" borderColor="neutral.100">
              <FrontPageSmall />
            </Box>
            <Box>
              <Heading as="h1" size="lg" mb="4">
                Din bærekraftsrapport
              </Heading>
              {!isVsmeMethod(method?.name) && (
                <Text mb="4">
                  Målingen uttrykker deres virksomhets bærekraft på et forhåndsdefinert indikatorsett, hvor resultatene
                  gis en referanse mot måltall der det eksisterer. Resultat presenteres oppsummert i en sol og per
                  indikator.
                </Text>
              )}
              {isFree && (
                <Text>
                  Ønsker du en fullverdig rapport med kontroll utført av en objektiv tredjepart?{" "}
                  <Link onClick={() => setHelpPopoverState("upgradeForm")} textDecoration="underline">
                    Bestill kontroll her
                  </Link>
                </Text>
              )}
              <Flex mt="8" gap="4">
                {isFree && (
                  <Button onClick={() => setHelpPopoverState("upgradeForm")} colorScheme="blue">
                    Be om tredjepartskontroll
                  </Button>
                )}
                <Button
                  leftIcon={<MdDownload style={{ marginTop: "2px" }} />}
                  onClick={() => downloadReport()}
                  variant="outline"
                >
                  Last ned rapport
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </RatingAccount.Provider>
    )
  )
}

const V4FrontPageSmall: React.FC = () => {
  const { ratingState, isFree } = useRating()
  const { accountTitle } = useRatingAccount()
  const brreg = ratingState?.brreg

  return (
    <Box bg="white" p="16px 32px">
      {isFree && (
        <Heading mb="4px" fontWeight={400} fontSize={"70%"} textAlign={"center"}>
          GRATIS -{" "}
          <Text as="span" fontWeight={700} fontSize="inherit">
            UFULLSTENDIG, IKKE VERIFISERT
          </Text>
        </Heading>
      )}
      <Heading fontWeight={500} mb="12px" fontSize={"150%"} textAlign={"center"}>
        BÆREKRAFTRAPPORT
      </Heading>
      <Box textAlign={"center"} fontSize="60%" mb="16px" fontWeight="400">
        PRODUSERT AV {accountTitle} - {new Date(ratingState.completedAt).getFullYear()}
      </Box>
      <Box position={"relative"}>
        <FreeVersionWatermark size="180%" />
        <Image alt="" width="384px" height="384px" src={frontpageImgSmall} />
        <Box position="absolute" top="0" left="6" w="30%">
          <Logo />
        </Box>
      </Box>
      <Box mt="12px" mb="2px" textAlign={"center"} fontSize="90%" fontWeight="300">
        {brreg?.navn}
      </Box>
      <Box textAlign={"center"} fontSize="60%" mb="4px" fontWeight="300">
        {brreg?.organisasjonsnummer.substring(0, 3)} {brreg?.organisasjonsnummer.substring(3, 6)}{" "}
        {brreg?.organisasjonsnummer.substring(6, 9)}
      </Box>
      <Box textAlign={"center"} fontSize="60%" fontWeight="300">
        {ratingState?.period?.toPrettyString()}
      </Box>
    </Box>
  )
}

const VsmeFrontPageSmall: React.FC = () => {
  const { ratingState } = useRating()

  return (
    <Box bg={`url("${frontpageImgUrl}")`} pt="1px" pl="30px" h="638px" backgroundSize="cover" color="white">
      <Box w="25%" my="-12px">
        <Logo />
      </Box>
      <Box position={"relative"} mt="250px" mb="350px">
        <FreeVersionWatermark size="180%" />
      </Box>
      <Heading as="h1" fontSize="28px">
        Bærekraftrapport
      </Heading>
      <Box fontSize="14px" textTransform="uppercase" lineHeight="17px" width={200} mt="1" pb={8}>
        Utarbeidet i tråd med VSME-standarden
      </Box>
      <Box fontSize="11px" fontWeight={600} mb={2}>
        {ratingState?.brreg?.navn}
      </Box>
      <Box fontSize="9px">
        <Mark color="white" fontWeight={500}>
          Org nr:{" "}
        </Mark>
        {ratingState?.brreg?.organisasjonsnummer}
      </Box>
      <Box fontSize="9px">
        <Mark color="white" fontWeight={500}>
          Rapportperiode:{" "}
        </Mark>
        <Box as="span" textTransform="lowercase">
          {ratingState?.period?.toPrettyString()}
        </Box>
      </Box>
      <Box fontSize="9px">
        <Mark color="white" fontWeight={500}>
          Publisert:{" "}
        </Mark>
        <Box as="span" textTransform="lowercase">
          {formatReportPublishedDate(ratingState)}
        </Box>
      </Box>
    </Box>
  )
}

export const FrontPageSmall: React.FC = () => {
  const { method } = useRating()

  if (!method) return null

  if (isVsmeMethod(method?.name)) {
    return <VsmeFrontPageSmall />
  }

  return <V4FrontPageSmall />
}
